import { logger } from '../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import * as satellite from 'satellite.js'
import { tleService, CONSTELLATIONS, type ConstellationName, type GpRecord } from './tle-service.js'
import { timelineService, type TimelineState } from '../timeline/timeline-service.js'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'

const log = logger.create('sat-constellation')

const SOURCE_ID = 'satellite-constellation-source'
const CIRCLE_LAYER_ID = 'satellite-constellation-circles'
const LABEL_LAYER_ID = 'satellite-constellation-labels'
const GROUP_ID = 'satellite'
const DEFAULT_OPACITY = 0.9

function propagate(
  record: GpRecord,
  date: Date
): { lng: number; lat: number; altKm: number } | null {
  try {
    const satrec = satellite.json2satrec(record as any)
    const posVel = satellite.propagate(satrec, date)
    if (!posVel.position || typeof posVel.position === 'boolean') return null

    const gmst = satellite.gstime(date)
    const geo = satellite.eciToGeodetic(posVel.position, gmst)

    return {
      lng: satellite.degreesLong(geo.longitude),
      lat: satellite.degreesLat(geo.latitude),
      altKm: geo.height,
    }
  } catch {
    return null
  }
}

class SatelliteConstellationService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _enabledConstellations = new Set<ConstellationName>(CONSTELLATIONS.map(c => c.id))
  private _opacity = DEFAULT_OPACITY
  private tleCache = new Map<ConstellationName, GpRecord[]>()
  private loading = false
  private timelineHandler: ((state: Readonly<TimelineState>) => void) | null = null

  get enabled(): boolean {
    return this._enabled
  }

  get opacity(): number {
    return this._opacity
  }

  get enabledConstellations(): ReadonlySet<ConstellationName> {
    return this._enabledConstellations
  }

  isConstellationEnabled(id: ConstellationName): boolean {
    return this._enabledConstellations.has(id)
  }

  register(map: maplibregl.Map): void {
    this.map = map
  }

  unregister(): void {
    this.disable()
    this.map = null
  }

  async enable(): Promise<void> {
    if (!this.map || this._enabled) return
    this._enabled = true
    await this.fetchTles()
    this.ensureSourceAndLayers()
    this.updatePositions(timelineService.getState().currentTime)
    this.timelineHandler = state => {
      this.updatePositions(state.currentTime)
    }
    timelineService.addListener(this.timelineHandler)
    log.info('Satellite constellation overlay enabled')
  }

  disable(): void {
    this._enabled = false
    if (this.timelineHandler) {
      timelineService.removeListener(this.timelineHandler)
      this.timelineHandler = null
    }
    this.removeLayers()
    this.removeSource()
    log.info('Satellite constellation overlay disabled')
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else void this.enable()
  }

  toggleConstellation(id: ConstellationName): void {
    if (this._enabledConstellations.has(id)) {
      this._enabledConstellations.delete(id)
    } else {
      this._enabledConstellations.add(id)
    }
    if (this._enabled) {
      this.updatePositions(timelineService.getState().currentTime)
    }
  }

  setEnabledConstellations(ids: ConstellationName[]): void {
    this._enabledConstellations = new Set(ids)
    if (this._enabled) {
      this.updatePositions(timelineService.getState().currentTime)
    }
  }

  setOpacity(value: number): void {
    this._opacity = value
    if (!this.map || !this._enabled) return
    if (this.map.getLayer(CIRCLE_LAYER_ID)) {
      this.map.setPaintProperty(CIRCLE_LAYER_ID, 'circle-opacity', value)
      this.map.setPaintProperty(CIRCLE_LAYER_ID, 'circle-stroke-opacity', value)
    }
    if (this.map.getLayer(LABEL_LAYER_ID)) {
      this.map.setPaintProperty(LABEL_LAYER_ID, 'text-opacity', value)
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) return
    this.ensureSourceAndLayers()
    this.updatePositions(timelineService.getState().currentTime)
  }

  private async fetchTles(): Promise<void> {
    if (this.loading) return
    this.loading = true
    try {
      const fetches = CONSTELLATIONS.map(async c => {
        const data = await tleService.getConstellation(c.id)
        this.tleCache.set(c.id, data)
      })
      await Promise.all(fetches)
      const total = [...this.tleCache.values()].reduce((s, d) => s + d.length, 0)
      log.info(`Loaded TLEs: ${total} satellites across ${this.tleCache.size} constellations`)
    } finally {
      this.loading = false
    }
  }

  private ensureSourceAndLayers(): void {
    if (!this.map) return

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }

    if (!this.map.getLayer(CIRCLE_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: CIRCLE_LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          paint: {
            'circle-radius': 5,
            'circle-color': ['get', 'color'],
            'circle-opacity': this._opacity,
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }

    if (!this.map.getLayer(LABEL_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LABEL_LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          layout: {
            'text-field': ['get', 'name'],
            'text-size': 10,
            'text-offset': [0, 1.2],
            'text-anchor': 'top',
            'text-optional': true,
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': ['get', 'color'],
            'text-halo-color': '#000000',
            'text-halo-width': 1,
            'text-opacity': this._opacity,
          },
          minzoom: 4,
        },
        GROUP_ID
      )
    }
  }

  private updatePositions(time: Date): void {
    if (!this.map || !this._enabled) return

    const features: GeoJSON.Feature[] = []

    for (const c of CONSTELLATIONS) {
      if (!this._enabledConstellations.has(c.id)) continue
      const records = this.tleCache.get(c.id) ?? []

      for (const rec of records) {
        const pos = propagate(rec, time)
        if (!pos) continue
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [pos.lng, pos.lat] },
          properties: {
            name: rec.OBJECT_NAME,
            color: CONSTELLATIONS.find(cn => cn.id === c.id)?.color ?? '#ffffff',
            constellation: c.id,
            altKm: pos.altKm,
          },
        })
      }
    }

    log.info(`Updated ${features.length} satellite positions at ${time.toISOString()}`)
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const source = this.map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined
    source?.setData({ type: 'FeatureCollection', features })
  }

  private removeLayers(): void {
    if (!this.map) return
    if (this.map.getLayer(LABEL_LAYER_ID)) this.map.removeLayer(LABEL_LAYER_ID)
    if (this.map.getLayer(CIRCLE_LAYER_ID)) this.map.removeLayer(CIRCLE_LAYER_ID)
  }

  private removeSource(): void {
    if (!this.map) return
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }
}

export const satelliteConstellationService = new SatelliteConstellationService()
